import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Trophy, Medal, Crown, User, Flame, Users } from "lucide-react";

export default function Leaderboard() {
  const [params, setParams] = useSearchParams();
  const classId = params.get("class") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [metric, setMetric] = useState("accuracy");
  const [myClasses, setMyClasses] = useState([]);

  useEffect(() => {
    api.get("/classes/me").then((r) => setMyClasses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ days: String(days), metric });
    if (classId) q.set("class_id", classId);
    api.get(`/leaderboard?${q.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days, metric, classId]);

  const rankBadge = (rank) => {
    if (rank === 1) return { icon: Crown, cls: "bg-yellow-400 text-yellow-900 border-yellow-500" };
    if (rank === 2) return { icon: Medal, cls: "bg-neutral-300 text-foreground border-neutral-400" };
    if (rank === 3) return { icon: Medal, cls: "bg-orange-300 text-orange-900 border-orange-500" };
    return { icon: User, cls: "bg-card text-foreground/80 border-border" };
  };

  const setClass = (id) => {
    if (id) params.set("class", id);
    else params.delete("class");
    setParams(params, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">
            Leaderboard {data?.class_name ? `· ${data.class_name}` : "· Public"}
          </div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" data-testid="leaderboard-title">
            <Trophy className="w-10 h-10 text-brand-gold" /> Top performers
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            {classId ? "Private class leaderboard — only members appear here." : "Everyone practising StenoSync ranked by recent performance."}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Class picker */}
          <div className="inline-flex border border-border flex-wrap" role="tablist">
            <button
              onClick={() => setClass("")}
              data-testid="lb-scope-public"
              className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
                !classId ? "bg-brand-blue text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
              }`}
            >
              Public
            </button>
            {myClasses.map((c) => (
              <button
                key={c.id}
                onClick={() => setClass(c.id)}
                data-testid={`lb-scope-class-${c.id}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border-l border-border transition-colors ${
                  classId === c.id ? "bg-brand-blue text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                <Users className="w-3 h-3 inline mr-1" /> {c.name}
              </button>
            ))}
          </div>

          {/* Range */}
          <div className="inline-flex border border-border" role="tablist">
            {[
              { k: 7, l: "This week" },
              { k: 30, l: "This month" },
              { k: 365, l: "All time" },
            ].map((o, i) => (
              <button
                key={o.k}
                onClick={() => setDays(o.k)}
                data-testid={`lb-range-${o.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  days === o.k ? "bg-brand-gold text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>

          {/* Metric */}
          <div className="inline-flex border border-border" role="tablist">
            {[
              { k: "accuracy", l: "By accuracy" },
              { k: "wpm", l: "By WPM" },
            ].map((o, i) => (
              <button
                key={o.k}
                onClick={() => setMetric(o.k)}
                data-testid={`lb-metric-${o.k}`}
                className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${i > 0 ? "border-l border-border" : ""} transition-colors ${
                  metric === o.k ? "bg-brand-blue text-white" : "bg-white text-muted-foreground hover:bg-secondary/60"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-muted-foreground">Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="border-2 border-dashed border-border p-10 text-center font-mono text-muted-foreground" data-testid="leaderboard-empty">
            No attempts in this range yet. Be the first to make the board.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto" data-testid="leaderboard-table">
            <table className="w-full text-sm font-mono min-w-[820px]">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold w-16">Rank</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Student</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Streak</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Attempts</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Avg Accuracy</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Avg WPM</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-[0.2em] font-bold">Best</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const b = rankBadge(r.rank);
                  const Icon = b.icon;
                  const streakHot = r.streak_days >= 7;
                  return (
                    <tr
                      key={r.user_id}
                      className={`border-t border-border ${r.is_me ? "bg-blue-50" : ""}`}
                      data-testid={`lb-row-${r.rank}`}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 border px-2 py-1 text-xs font-bold ${b.cls}`}>
                          <Icon className="w-3 h-3" /> {r.rank}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold">
                          {r.name}
                          {r.is_me && <span className="ml-1 text-[10px] uppercase tracking-widest text-brand-gold">· You</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.streak_days > 0 ? (
                          <span
                            className={`inline-flex items-center gap-1 border px-2 py-1 text-xs font-bold ${
                              streakHot ? "border-orange-500 bg-orange-50 text-orange-700" : "border-neutral-300 text-muted-foreground"
                            }`}
                            title={`${r.streak_days} day streak`}
                            data-testid={`lb-streak-${r.rank}`}
                          >
                            <Flame className={`w-3 h-3 ${streakHot ? "text-orange-500" : ""}`} /> {r.streak_days}d
                          </span>
                        ) : (
                          <span className="text-muted-foreground/70">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{r.attempts}</td>
                      <td className="px-4 py-3 text-right text-green-700 font-bold">{r.avg_accuracy}%</td>
                      <td className="px-4 py-3 text-right text-brand-gold font-bold">{r.avg_wpm}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {r.best_accuracy}% · {r.best_wpm} wpm
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
